import { Header } from '../../common/header.component';
import { Input } from '../../common/input.component';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import {
  CleanDeliveryMethod,
  CleanLocalDeliveryMethod,
} from '../../../export-types/cleaned-types';
import { api } from '../../../services/api';
import { AlertService } from '../../../services/alert.service';
import { useSuppliers } from '../../../api/suppliers';
import { required } from '../../../utils/validations';
import { Select } from '../../common/select.component';
import { useLocations } from '../../../api/locations';
import Toggle from 'rsuite/Toggle';
import { DeliveryMethodsMultiSelect } from '../../DeliveryMethodsMultiSelect';
import { MediaInput, MediaInputValue } from '../../common/media-input';
import { uploadMediaInputFiles } from '../../../services/file-service';
import { InputWrapper } from '../../common/input-wrapper';

type FieldValues = {
  name: string;
  slug: string;
  priority: number | null;
  supplierId: number;
  locationId: number;
  selfDelivery: boolean;
  isFulfillment: boolean;
  isCdek: boolean;
  cdekFee: string;
  priceClient?: string;
  commentClient?: string | null;
  priceSupplier?: string;
  commentSupplier?: string | null;
  minWeight?: string;
  maxWeight?: string;
  clientAdditionalInfoLabel?: string;
  filteredDeliveryMethods: CleanDeliveryMethod[];
  logoFile?: MediaInputValue[];
};

export const LocalDeliveryMethodScreen = () => {
  const { data: suppliers } = useSuppliers();
  const { data: locations } = useLocations();
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: Partial<CleanLocalDeliveryMethod>;
  };

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      name: state?.name,
      slug: state?.slug,
      priority: state?.priority,
      supplierId: state?.supplier?.id,
      locationId: state?.location?.id,
      selfDelivery: state?.selfDelivery ?? false,
      isFulfillment: state?.isFulfillment ?? false,
      isCdek: state?.isCdek ?? false,
      cdekFee: state?.cdekFee,
      priceClient: state?.priceClient,
      commentClient: state?.commentClient,
      priceSupplier: state?.priceSupplier,
      commentSupplier: state?.commentSupplier,
      minWeight: state?.minWeight,
      maxWeight: state?.maxWeight,
      clientAdditionalInfoLabel: state?.clientAdditionalInfoLabel || '',
      filteredDeliveryMethods: state?.filteredDeliveryMethods || [],
      logoFile: [
        ...(state?.logo
          ? [
              {
                type: 'exist' as 'exist',
                file: state?.logo,
              },
            ]
          : []),
      ],
    },
  });

  const isCdek = watch('isCdek');
  const supplierId = watch('supplierId');

  async function onSave(data: FieldValues) {
    const id = state?.id;
    const logo = data.logoFile
      ? await uploadMediaInputFiles(data.logoFile)
      : undefined;
    let promise;
    const processedData = {
      ...data,
      deliveryMethodIds: data.filteredDeliveryMethods?.map(
        (deliveryMethod) => deliveryMethod.id,
      ),
      logoFileId: logo?.length ? logo[0]?.file?.id : undefined,
    };
    if (id) {
      if (processedData.priceClient === '') {
        processedData.priceClient = '0';
      }
      if (processedData.priceSupplier === '') {
        processedData.priceSupplier = '0';
      }
      promise = api.put(`/local-delivery-method/${id}`, processedData);
    } else {
      promise = api.post(`/local-delivery-method`, processedData);
    }

    promise.then(() => {
      AlertService.success();
      navigate('/deliveries/local-delivery');
    });
  }

  return (
    <>
      <Header />
      <div className="container" style={{ marginTop: -70 }}>
        <h1 className="mb-4">Добавить транспортную компанию</h1>
        <form onSubmit={handleSubmit(onSave)}>
          <Controller
            name="supplierId"
            control={control}
            rules={required}
            render={({ field }) => (
              <Select
                className="mb-3"
                title="Поставщик"
                placeholder="Выберите поставщика"
                options={
                  suppliers?.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  })) ?? []
                }
                {...field}
                error={errors.supplierId?.message}
              />
            )}
          />
          <Controller
            name="locationId"
            control={control}
            rules={required}
            render={({ field }) => (
              <Select
                className="mb-3"
                title="Откуда"
                placeholder="Выберите локацию"
                options={
                  locations?.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  })) ?? []
                }
                {...field}
                error={errors.locationId?.message}
              />
            )}
          />
          <div className="row">
            <div className="col-6 mb-3">
              <Input
                title="Название"
                placeholder="Например, СДЭК"
                {...register('name', { required: 'Заполните поле' })}
                error={errors.name?.message}
              />
              <div className="fs-14 color-gray-400">
                Это название отобразится у клиента
              </div>
            </div>
          </div>
          {!state?.id ? (
            <div className="row">
              <div className="col-6 mb-3">
                <Input
                  title="Псевдоним"
                  {...register('slug', { required: 'Заполните поле' })}
                  error={errors.slug?.message}
                />
                <div className="fs-14 color-gray-400">
                  Внутреннее обозначение в БД
                </div>
              </div>
            </div>
          ) : null}
          <div className="row">
            <div className="col-6 mb-3">
              <Input
                title="Приоритет"
                type="number"
                step="1"
                {...register('priority')}
                error={errors.priority?.message}
              />
              <div className="fs-14 color-gray-400">
                Влияет на сортировку. Чем меньше, тем выше в списке
              </div>
            </div>
            <Controller
              name="selfDelivery"
              control={control}
              render={({ field }) => (
                <div className="flex items-center mb-3">
                  <Toggle checked={field.value} onChange={field.onChange} />
                  <div className="ml-2">Самовывоз</div>
                </div>
              )}
            />
            <Controller
              name="isFulfillment"
              control={control}
              render={({ field }) => (
                <div className="flex items-center mb-3">
                  <Toggle checked={field.value} onChange={field.onChange} />
                  <div className="ml-2">Фулфилмент</div>
                </div>
              )}
            />
            <Controller
              name="isCdek"
              control={control}
              render={({ field }) => (
                <div className="flex items-center mb-3">
                  <div className="ml-2">СДЭК доставка</div>

                  <Toggle checked={field.value} onChange={field.onChange} />
                </div>
              )}
            />
            {isCdek ? (
              <Controller
                name="cdekFee"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center mb-3 w-25">
                    <Input
                      title="CДЭК комиссия за доставку"
                      type="number"
                      step={0.01}
                      {...field}
                      error={errors.priority?.message}
                      postfix="%"
                    />
                  </div>
                )}
              />
            ) : null}
            <div className="row mb-3 ">
              <div className="col-6">
                <div className="mb-2">Минимальный вес доставки</div>
                <Input
                  className="flex-grow-1"
                  type="number"
                  min={0}
                  step={0.01}
                  {...register('minWeight')}
                  error={errors.minWeight?.message}
                  postfix="кг"
                />
              </div>

              <div className="col-6">
                <div className="mb-2">Максимальный вес доставки</div>
                <Input
                  className="flex-grow-1"
                  type="number"
                  min={0}
                  step={0.01}
                  {...register('maxWeight')}
                  error={errors.maxWeight?.message}
                  postfix="кг"
                />
              </div>
            </div>
            <p className="mb-2 mt-2">Дополнительные расходы связанные с ТК:</p>
            <div className="row">
              <div className="col-6 mb-3">
                <Input
                  title="Цена Клиент"
                  type="number"
                  placeholder=""
                  {...register('priceClient')}
                  error={errors.priceClient?.message}
                />
              </div>
              <div className="col-6 mb-3">
                <Input
                  title="Комментарий"
                  placeholder=""
                  {...register('commentClient')}
                  error={errors.commentClient?.message}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-6 mb-3">
                <Input
                  title="Цена Поставщик"
                  type="number"
                  placeholder=""
                  {...register('priceSupplier')}
                  error={errors.priceSupplier?.message}
                />
              </div>
              <div className="col-6 mb-3">
                <Input
                  title="Комментарий"
                  placeholder=""
                  {...register('commentSupplier')}
                  error={errors.commentSupplier?.message}
                />
              </div>
            </div>
            <div className={'row'}>
              <div className={'col-6'}>
                <p className="mb-2 mt-2">
                  Дополнительное сообщение на клиенте:
                </p>
                <div className="ml-1">
                  <Controller
                    control={control}
                    name="clientAdditionalInfoLabel"
                    render={({ field }) => (
                      <textarea
                        {...field}
                        className="form-control bg-gray w-100"
                        style={{ height: '300px' }}
                      />
                    )}
                  />
                </div>
              </div>

              <div className={'col-6'}>
                <p className="mb-2 mt-2">
                  Показывать только для выбранных способов доставки:
                </p>
                <div className="ml-1">
                  <Controller
                    control={control}
                    name="filteredDeliveryMethods"
                    render={({ field }) => (
                      <DeliveryMethodsMultiSelect
                        {...field}
                        supplierId={supplierId}
                        placeholder={'Выберите из списка'}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className={'row'}>
              <Controller
                control={control}
                name="logoFile"
                render={({ field, fieldState }) => (
                  <InputWrapper
                    theme="light"
                    className="mt-3"
                    title={'Логотип'}
                    error={fieldState.error?.message}
                  >
                    <MediaInput {...field} className="mt-2" multiple={false} />
                  </InputWrapper>
                )}
              />
            </div>
            <div className="col-12 pt-3 mb-4">
              <button type="submit" className="btn btn-success w-100">
                Сохранить
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
